import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button, { ButtonGroup } from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, { ModalTransition, ModalBody } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import PageHeader from '@atlassian/jira-common-components-page-header';
import { useIntl } from '@atlassian/jira-intl';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { HasPermission } from '../../../common/ui/has-permission/index.tsx';
import messages from './messages';

type Props = {
	isOpen: boolean;
	canCreate: boolean;
	children: ReactNode;
} & Intl & {
		onAddCalendar: () => void;
		onClose: () => undefined | Promise<undefined>;
	};

const CalendarModalDialogView = ({
	isOpen,
	canCreate,
	onClose,
	onAddCalendar,
	children,
}: Props) => {
	const { formatMessage } = useIntl();
	const actions = (
		<ButtonGroup>
			<HasPermission hasPermission={canCreate}>
				<Button
					onClick={onAddCalendar}
					testId="servicedesk-sla-settings.ui.calendars.modal.add-button"
				>
					{formatMessage(messages.addCalendar)}
				</Button>
			</HasPermission>
			<Button
				onClick={onClose}
				appearance="subtle"
				iconAfter={<CrossIcon label="Close" />}
				testId="servicedesk-sla-settings.ui.calendars.modal.close-button"
			/>
		</ButtonGroup>
	);
	const header = () => (
		<HeaderContainer>
			<PageHeader actions={actions}>
				<Box xcss={calendarHeadingStyles}>{formatMessage(messages.caledarTitle)}</Box>
			</PageHeader>
		</HeaderContainer>
	);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<ModalDialog width="large" height="100%" autoFocus onClose={onClose}>
						{header()}
						<ModalBody>{children}</ModalBody>
					</ModalDialog>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

export const CalendarModalDialog = CalendarModalDialogView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderContainer = styled.div({
	padding: `${token('space.0', '0px')} ${token('space.200', '16px')}`,
});

const calendarHeadingStyles = xcss({
	marginLeft: 'space.100',
});
